import type { SVGProps } from 'react';

type Props = SVGProps<SVGSVGElement> & { size?: number };

function Base({ size = 16, children, ...rest }: Props & { children: React.ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconMapPin = (p: Props) => (
  <Base {...p}>
    <path d="M20 10c0 7-8 12-8 12s-8-5-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </Base>
);

export const IconClock = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </Base>
);

export const IconTag = (p: Props) => (
  <Base {...p}>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </Base>
);

export const IconAlert = (p: Props) => (
  <Base {...p}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </Base>
);

export const IconCheckCircle = (p: Props) => (
  <Base {...p}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </Base>
);

export const IconXCircle = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </Base>
);

export const IconRotate = (p: Props) => (
  <Base {...p}>
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </Base>
);

export const IconLoader = (p: Props) => (
  <Base {...p} className={`icon--spin ${p.className ?? ''}`.trim()}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </Base>
);

// --- Nature / city icons --------------------------------------------------

export const IconMountain = (p: Props) => (
  <Base {...p}>
    <path d="M8 3l4 8 5-5 5 11H2z" />
  </Base>
);

export const IconTrees = (p: Props) => (
  <Base {...p}>
    <path d="M10 10a4 4 0 0 0-4-4 4 4 0 0 0-4 4c0 2 2 3 4 3a4 4 0 0 0 4-3Z" />
    <path d="M6 13v8" />
    <path d="M22 10a4 4 0 0 0-4-4 4 4 0 0 0-4 4c0 2 2 3 4 3a4 4 0 0 0 4-3Z" />
    <path d="M18 13v8" />
  </Base>
);

export const IconWaves = (p: Props) => (
  <Base {...p}>
    <path d="M2 6c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" />
    <path d="M2 12c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" />
    <path d="M2 18c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" />
  </Base>
);

export const IconSun = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m4.93 19.07 1.41-1.41" />
    <path d="m17.66 6.34 1.41-1.41" />
  </Base>
);

export const IconLeaf = (p: Props) => (
  <Base {...p}>
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6" />
  </Base>
);

export const IconDroplet = (p: Props) => (
  <Base {...p}>
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
  </Base>
);

// --- Sport / adventure icons ---------------------------------------------

export const IconBike = (p: Props) => (
  <Base {...p}>
    <circle cx="5.5" cy="17.5" r="3.5" />
    <circle cx="18.5" cy="17.5" r="3.5" />
    <circle cx="15" cy="5" r="1" />
    <path d="M12 17.5V14l-3-3 4-3 2 3h2" />
  </Base>
);

export const IconCompass = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </Base>
);

export const IconTent = (p: Props) => (
  <Base {...p}>
    <path d="M3.5 21 12 4l8.5 17Z" />
    <path d="M12 4v17" />
    <path d="M7 21l5-7 5 7" />
  </Base>
);

export const IconHiking = (p: Props) => (
  <Base {...p}>
    <path d="m5 20 3-5 2 3 4-7 3 5 2-3" />
    <path d="M20 20H4" />
  </Base>
);

export const IconFlower = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="2" />
    <path d="M12 5a3 3 0 0 0-3 3c0 1 .5 2 3 4 2.5-2 3-3 3-4a3 3 0 0 0-3-3Z" />
    <path d="M12 19a3 3 0 0 0 3-3c0-1-.5-2-3-4-2.5 2-3 3-3 4a3 3 0 0 0 3 3Z" />
    <path d="M5 12a3 3 0 0 1 3-3c1 0 2 .5 4 3-2 2.5-3 3-4 3a3 3 0 0 1-3-3Z" />
    <path d="M19 12a3 3 0 0 1-3 3c-1 0-2-.5-4-3 2-2.5 3-3 4-3a3 3 0 0 1 3 3Z" />
  </Base>
);

export const IconFire = (p: Props) => (
  <Base {...p}>
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.1-2-.2-4 2-6 .5 2.5 2 5 4 6.5 1.8 1.4 3 3.3 3 5.5a7 7 0 1 1-14 0c0-1.2.4-2.3 1-3a2.5 2.5 0 0 0 2.5 2.5Z" />
  </Base>
);

export const IconKayak = (p: Props) => (
  <Base {...p}>
    <path d="M2 17c2 2 8 3 10 3s8-1 10-3" />
    <path d="M7 16l5-9 5 9" />
    <circle cx="12" cy="4" r="1.5" />
  </Base>
);

export const IconBinoculars = (p: Props) => (
  <Base {...p}>
    <circle cx="6" cy="15" r="3" />
    <circle cx="18" cy="15" r="3" />
    <line x1="9" y1="15" x2="15" y2="15" />
    <path d="M6 5H5.5A1.5 1.5 0 0 0 4 6.5V12" />
    <path d="M8 5v7" />
    <path d="M16 5v7" />
    <path d="M18 5h.5A1.5 1.5 0 0 1 20 6.5V12" />
  </Base>
);

export const IconFish = (p: Props) => (
  <Base {...p}>
    <path d="M6 12c5-5 8-5 12 0-4 5-7 5-12 0Z" />
    <path d="M18 12l4-4v8Z" />
    <circle cx="9" cy="11" r="0.5" />
  </Base>
);

export const IconWaterfall = (p: Props) => (
  <Base {...p}>
    <g
      transform="translate(2.3 0) scale(0.31) translate(-55.841706 -102.7197)"
      fill="currentColor"
      stroke="none"
    >
      <path d="m 76.242805,102.75335 c -3.176918,0.47174 -6.094712,4.7468 -7.667812,7.19745 -5.746748,8.95256 -3.708046,21.63687 -3.708046,31.75 v 10.58333 c 0.0017,1.03168 -0.05433,3.23129 1.562796,3.05169 1.396972,-0.15514 1.082977,-2.36254 1.083037,-3.31627 v -10.58334 c 0,-9.4411 -1.751217,-20.83665 3.083743,-29.36875 1.731499,-3.05552 4.317695,-4.57007 6.621525,-7.01615 0.840804,-0.89273 0.440433,-2.50817 -0.975243,-2.29796 m 12.701023,0.0278 c -3.50115,1.02802 -6.2875,4.7323 -8.041826,7.69884 -5.266343,8.90531 -3.335055,21.05424 -3.335055,30.95625 0,3.70399 -0.01933,7.40857 2.04e-4,11.1125 0.0052,0.99541 0.162928,3.29644 1.792469,2.69851 1.206875,-0.44284 0.85316,-2.50678 0.85316,-3.49226 v -11.1125 c 0,-9.09349 -1.689486,-20.04224 2.874688,-28.31041 1.84817,-3.34805 5.12866,-4.66477 7.28319,-7.50777 0.7993,-1.05471 -0.1758,-2.41049 -1.42683,-2.04316 m 12.700002,0.007 c -3.182772,0.78348 -5.850732,4.58655 -7.488772,7.16315 -5.63213,8.85916 -3.62353,21.22159 -3.62353,31.22083 v 11.1125 c 0.002,1.02273 -0.0571,3.24413 1.5628,3.02107 1.39604,-0.19224 1.08297,-2.31088 1.08303,-3.28565 v -10.84792 c 0,-9.15687 -1.78644,-20.24351 2.7275,-28.575 1.83392,-3.38491 4.718132,-4.85947 6.976752,-7.69762 0.83634,-1.05093 0.18578,-2.46178 -1.23778,-2.11136 m 12.69898,-0.0133 c -3.53556,0.79066 -6.76183,5.49581 -8.26456,8.49933 -4.55936,9.11289 -2.84672,20.53598 -2.84672,30.42709 v 10.84791 c 0.002,0.97003 -0.0525,3.19666 1.57607,2.72199 1.39509,-0.40663 1.06976,-2.4211 1.06976,-3.51574 V 141.1721 c 0,-9.14864 -1.90764,-20.56899 2.77915,-28.83958 1.81898,-3.20989 4.52654,-4.73843 6.8706,-7.34485 0.88776,-0.98713 0.2,-2.5224 -1.1843,-2.21282 m -13.75711,54.40924 c -2.657882,0.56278 -4.683512,3.65228 -7.408342,3.49348 -1.65992,-0.0967 -2.64775,-1.51872 -3.96977,-2.31286 -1.47418,-0.88555 -3.62085,-1.04826 -5.29064,-0.68514 -2.31323,0.50304 -3.888667,3.23655 -6.350001,3.11294 -2.64526,-0.13285 -4.123501,-3.14835 -6.879167,-3.43469 -3.631403,-0.37733 -5.059645,2.69693 -8.202083,3.33302 -2.363518,0.47842 -3.477377,-2.52213 -5.540735,-2.20793 -3.138297,0.47791 1.797699,4.38821 2.894902,4.70534 1.972225,0.57004 4.118061,0.21517 5.820833,-0.87337 1.288552,-0.82374 2.595051,-2.30131 4.233333,-2.30183 2.690501,-8.7e-4 4.10348,3.21341 6.879167,3.37262 3.738761,0.21444 5.186501,-2.77655 8.466671,-3.20134 2.87491,-0.37231 4.71455,3.06215 7.67291,3.20215 3.48471,0.16492 5.10609,-3.00903 8.202082,-3.39344 2.26305,-0.28099 3.29884,1.50649 5.02709,2.55376 2.5382,1.53807 5.62991,1.25579 7.9373,-0.54938 0.61222,-0.47897 1.4365,-1.05217 1.73938,-1.7988 0.34996,-0.86267 -0.2657,-1.7781 -1.2141,-1.70917 -1.98972,0.14461 -3.07163,2.77421 -5.28758,2.3741 -3.33353,-0.60188 -4.80942,-4.50988 -8.73125,-3.67946 m -31.48542,8.19187 c -2.725641,0.42361 -4.712026,3.61523 -7.408333,3.44428 -1.734527,-0.10996 -2.511646,-1.62734 -3.968546,-2.22548 -0.783493,-0.32166 -1.645545,0.17223 -1.707746,1.04078 -0.08731,1.21917 1.332902,2.2708 2.236709,2.83325 2.266295,1.41034 4.853983,1.46359 7.14375,0.0906 1.381192,-0.82816 2.53251,-2.24877 4.233333,-2.36798 2.944246,-0.20635 4.604447,3.26156 7.672916,3.39691 3.405653,0.15023 4.916845,-2.98278 7.937505,-3.36813 2.89337,-0.36911 4.92843,3.18591 7.93749,3.35037 3.28438,0.17951 4.98891,-3.00454 7.937502,-3.5684 2.08268,-0.39827 3.48317,1.55754 5.02709,2.57928 2.1398,1.41607 4.99317,1.3641 7.14375,0.009 0.89751,-0.56537 4.10359,-2.87332 2.05521,-3.95691 -1.77956,-0.94139 -3.93897,2.51663 -6.02396,2.21874 -2.62943,-0.37568 -4.04458,-3.30875 -6.87917,-3.57595 -3.722242,-0.35088 -5.218232,2.86524 -8.466672,3.35526 -2.95623,0.44595 -4.85981,-3.0035 -7.93749,-3.10681 -3.38341,-0.11356 -4.95609,2.96188 -7.937505,3.31424 -3.33256,0.39385 -5.237741,-4.04754 -8.995833,-3.46348 m 31.48542,7.93464 c -2.696282,0.49568 -4.647822,3.64708 -7.408342,3.50573 -3.46227,-0.17727 -4.9064,-4.02931 -8.99583,-3.09558 -2.35124,0.53686 -4.168683,3.28763 -6.614581,3.13744 -2.616755,-0.16069 -4.149597,-3.12118 -6.879167,-3.37936 -3.748665,-0.35457 -5.64674,3.51409 -8.995833,3.37283 -1.673875,-0.0706 -3.636784,-3.05741 -5.207351,-2.06604 -2.539765,1.60313 3.18183,4.52526 4.413601,4.74616 3.885537,0.69681 5.612912,-3.45913 8.995833,-3.36363 2.922928,0.0825 4.507473,3.51866 7.672917,3.54206 3.103745,0.023 4.634481,-2.86017 7.408331,-3.29197 3.19483,-0.49735 4.98121,3.06778 8.20208,3.11354 3.41714,0.0486 5.13479,-3.29402 8.202092,-3.55002 1.86965,-0.15605 3.10387,1.54305 4.49791,2.46882 2.518,1.6722 5.15537,1.69141 7.67292,0.0171 0.73619,-0.48959 1.68693,-1.05618 2.10809,-1.86535 0.45129,-0.86705 0.0439,-2.03845 -1.05486,-1.97988 -2.12853,0.11344 -3.10591,2.72297 -5.55115,2.22895 -3.23075,-0.65272 -4.61843,-4.24828 -8.46666,-3.54085 z" />
    </g>
  </Base>
);
